
import { ALL_PLANOS, ProSolutoAMinimoParaCinquentaCinquenta } from '../utils/constantes';

// ===== CONSTANTES DE REGRAS EXTRAS =====
export const TIPO_REGRA_EXTRA = {
  NA: 0,
  DISTRIBUICAO_CARTEIRA_FB: 1,
  DISTRIBUICAO_CARTEIRA_SAD: 2,
  DISTRIBUICAO_CARTEIRA_LINEAR: 3,
  CORRECAO_FB: 4,
  CORRECAO_SAD: 5,
  CORRECAO_LINEAR: 6
};

// Mapeamento: nomeRegraExtra (string da API) → chave interna de TIPO_FLUXO
const NOME_REGRA_TO_FLUXO_KEY = {
  '100/0': '1',
  '20/80': '2',
  '30/70': '3',
  '40/60': '4',
  '50/50': '5',
  '35/65': '6',
  '45/55': '7'
};

// Mapeamento: nomeRegraExtra (string da API) → chave interna de TIPO_CORRECAO
const NOME_REGRA_TO_CORRECAO_KEY = {
  'SemCorrecao': 'SemCorrecao',
  'IPCA': 'IPCA',
  'IPCA_075': 'IPCA_075',
  'IPCA_085': 'IPCA_085',
  'IPCA_095': 'IPCA_095',
  'IPCA_099': 'IPCA_099',
  'IPCA_1': 'IPCA_1',
  'INCC_IPCA_1': 'INCC_IPCA_1',
  'FLEX_IPCA_1': 'FLEX_IPCA_1',
  'SAD_IPCA_099': 'SAD_IPCA_099'
};

/**
 * Filtra regrasExtra pelo tipo desejado e converte nomeRegraExtra para chave interna.
 * Valores inválidos de nomeRegraExtra são descartados automaticamente.
 *
 * @param {Array<{regraExtra: number, nomeRegraExtra: string}>} regrasExtra
 * @param {number} tipoRegra - Tipo de regra a filtrar (1 a 6)
 * @param {Object} mapeamento - NOME_REGRA_TO_FLUXO_KEY ou NOME_REGRA_TO_CORRECAO_KEY
 * @returns {Array<string>} Chaves internas válidas
 */
const extrairRegrasDoTipo = (regrasExtra, tipoRegra, mapeamento) => {
  if (!Array.isArray(regrasExtra)) return [];
  return regrasExtra
    .filter(r => r.regraExtra === tipoRegra)
    .map(r => mapeamento[r.nomeRegraExtra])
    .filter(Boolean);
};

// ===== GERENCIADOR DE DADOS DO IFRAME =====

let iframeDataCache = null;

/**
 * Obtém dados do iframe via postMessage ou cache
 */
export const getIframeData = () => {
  // Cache
  if (iframeDataCache) return iframeDataCache;
  
  // postMessage ou globalThis._iframeData
  if (globalThis._iframeData) {
    iframeDataCache = globalThis._iframeData;
    return globalThis._iframeData;
  }

  return null;
};


/**
 * Limpa cache
 */
export const clearIframeDataCache = () => {
  iframeDataCache = null;
  globalThis._iframeData = null;
};

/**
 * Obtém valor específico dos dados
 */
export const getIframeDataValue = (key, defaultValue = null) => {
  const data = getIframeData();
  return data?.[key] ?? defaultValue;
};

// ===== FUNÇÕES DE SIMULAÇÃO FLEX =====

/**
 * Calcula os planos disponíveis baseado nas regras de negócio
 * @param {number} parcelasPsaRegra - Valor de prosolutoA da unidade (parcelas_psa_regra)
 * @param {string} linhaProduto - Linha de produto (CLASS, SENSIA, etc)
 * @param {boolean} isRendaExterior - Se cliente possui renda exterior
 * @returns {Array} Lista de planos disponíveis
 */
export const calcularPlanosDisponiveis = (parcelasPsaRegra, linhaProduto, isRendaExterior) => {
  let planosDisponiveis = [];

  if (parcelasPsaRegra === 0) {
    planosDisponiveis = [
      ALL_PLANOS.AVista,
      ALL_PLANOS.FinanciamentoBancario
    ];
  } else if (parcelasPsaRegra <= 1) {
    planosDisponiveis = [
      ALL_PLANOS.AVista,
      ALL_PLANOS.PrazoObra,
      ALL_PLANOS.FinanciamentoBancario
    ];
  } else {
    planosDisponiveis = [
      ALL_PLANOS.AVista,
      ALL_PLANOS.PrazoObra,
      ALL_PLANOS.Fixo12,
      ALL_PLANOS.PosObra
    ];

    // Adicionar Financiamento Bancário apenas se não for renda exterior e for CLASS ou SENSIA
    const linhaProdutoNormalizada = linhaProduto?.toUpperCase() || '';
    if ((linhaProdutoNormalizada === 'CLASS' || linhaProdutoNormalizada === 'SENSIA') && !isRendaExterior) {
      planosDisponiveis.push(ALL_PLANOS.FinanciamentoBancario);
    }
  }

  return planosDisponiveis;
};

/**
 * Calcula fluxos para Pós Obra baseado nas regras de negócio
 * @private
 */
const calcularFluxoPosObra = (additionalParams) => {
  const { isRendaExterior = false, unidadeData, eSAD = false, regrasExtra = [] } = additionalParams;
  const parcelasPSA = unidadeData?.prosolutoA ?? 0;
  const parcelasPsaRegra = Math.min(parcelasPSA, 36);

  // 1) Fluxo padrão calculado pelas regras de PSA (sem mudança)
  let padraoFluxoPosObra = [];
  if (eSAD) {
    padraoFluxoPosObra = calcularFluxoPosObraSAD(parcelasPsaRegra);
  } else {
    padraoFluxoPosObra = calcularFluxoPosObraSemSAD(parcelasPsaRegra, isRendaExterior);
  }

  // 2) Fluxos extras vindos da configuração ADM:
  //    - Se eSAD=true  → buscar regras tipo 2 (DISTRIBUICAO_CARTEIRA_SAD)
  //    - Se eSAD=false → buscar regras tipo 3 (DISTRIBUICAO_CARTEIRA_LINEAR)
  const tipoDistribuicao = eSAD
    ? TIPO_REGRA_EXTRA.DISTRIBUICAO_CARTEIRA_SAD
    : TIPO_REGRA_EXTRA.DISTRIBUICAO_CARTEIRA_LINEAR;
  const fluxosExtras = extrairRegrasDoTipo(regrasExtra, tipoDistribuicao, NOME_REGRA_TO_FLUXO_KEY);

  // 3) Juntar padrão + extras sem duplicatas
  const tiposDisponiveis = [...new Set([...padraoFluxoPosObra, ...fluxosExtras])];

  console.log('[TipoFluxo] Pós Obra - eSAD:', eSAD, 'isRendaExterior:', isRendaExterior,
              'parcelasPSA:', parcelasPSA, 'regrasExtra:', regrasExtra, '→', tiposDisponiveis);

  return tiposDisponiveis;
};

/**
 * Calcula fluxos para Pós Obra com SAD
 * @private
 */
const calcularFluxoPosObraSAD = (parcelasPsaRegra) => {
  if (parcelasPsaRegra > 24 && parcelasPsaRegra <= 36) {
    return ['5']; // 50/50
  }
  if (parcelasPsaRegra > 12 && parcelasPsaRegra <= 24) {
    return ['7']; // 45/55
  }
  if (parcelasPsaRegra <= 12) {
    return ['6']; // 35/65
  }
  return [];
};

/**
 * Calcula fluxos para Pós Obra sem SAD
 * @private
 */
const calcularFluxoPosObraSemSAD = (parcelasPsaRegra, isRendaExterior) => {
  if (isRendaExterior) {
    if (parcelasPsaRegra <= 0) {
      return ['4']; // 40/60
    } else if (parcelasPsaRegra < 12) {
      return ['6']; // 35/65
    }
    return ['4']; // 40/60
  }
  return ['4']; // 40/60
};

/**
 * Calcula fluxos para Financiamento Bancário
 * @private
 */
const calcularFluxoFinanciamentoBancario = (additionalParams) => {
  const regrasExtra = additionalParams.regrasExtra ?? [];

  // 1) Fluxo padrão baseado em prosolutoA (sem mudança)
  const tiposDisponiveis = additionalParams.unidadeData.prosolutoA >= ProSolutoAMinimoParaCinquentaCinquenta ? ['5'] : ['2'];

  // 2) Fluxos extras tipo 1 (DISTRIBUICAO_CARTEIRA_FB) vindos do ADM
  const fluxosFB = extrairRegrasDoTipo(regrasExtra, TIPO_REGRA_EXTRA.DISTRIBUICAO_CARTEIRA_FB, NOME_REGRA_TO_FLUXO_KEY);
  fluxosFB.forEach(f => {
    if (!tiposDisponiveis.includes(f)) tiposDisponiveis.push(f);
  });

  console.log('[TipoFluxo] Financiamento Bancário - ProSolutoA:', additionalParams.unidadeData.prosolutoA, 'regrasExtra:', regrasExtra, '→', tiposDisponiveis);
  return tiposDisponiveis;
};

/**
 * Calcula os tipos de fluxos disponíveis baseado no plano selecionado
 * @param {string} planoSelecionado - Nome do plano selecionado (ex: 'À Vista', 'Prazo de Obra')
 * @param {object} additionalParams - Parâmetros adicionais para regras complexas (uso futuro)
 * @returns {Array<string>} Lista de chaves TIPO_FLUXO disponíveis
 */
export const calcularTipoFluxosDisponiveis = (planoSelecionado, additionalParams = {}) => {
  if (!planoSelecionado) {
    return []; // Sem plano selecionado, sem fluxos
  }

  // Planos com apenas fluxo 100/0
  const planosFluxoUnico = [
    ALL_PLANOS.AVista,
    ALL_PLANOS.PrazoObra,
    ALL_PLANOS.Fixo12,
    ALL_PLANOS.Fixo18
  ];

  // Verificar se o plano tem apenas fluxo único
  if (planosFluxoUnico.includes(planoSelecionado)) {
    console.log('[TipoFluxo] Plano com fluxo único:', planoSelecionado, '→ [1]');
    return ['1']; // 100/0
  }

  // Planos com regras complexas
  let tiposDisponiveis = [];

  switch (planoSelecionado) {
    case ALL_PLANOS.PosObra:
      tiposDisponiveis = calcularFluxoPosObra(additionalParams);
      break;

    case ALL_PLANOS.FinanciamentoBancario:
      tiposDisponiveis = calcularFluxoFinanciamentoBancario(additionalParams);
      break;

    default:
      console.warn('[TipoFluxo] Plano não reconhecido:', planoSelecionado);
      tiposDisponiveis = ['1']; // Default para 100/0
      break;
  }

  console.log('[TipoFluxo] Calculando para plano:', planoSelecionado, '→', tiposDisponiveis);
  return tiposDisponiveis;
}

/**
 * Calcula os tipos de correção para o plano Pós Obra baseado nas regras de negócio
 * @param {object} params - Parâmetros necessários para as regras
 * @param {number} params.quantidadeParcelas - Número de parcelas mensais (do input do usuário)
 * @param {boolean} params.eSAD - Se é SAD (Sem Avaliação de Demanda)
 * @param {Array<number>} params.regrasExtra - Array de regras extras (0=NA, 1=IPCA_IPCA_Mais_Um, 2=TrintaSetenta)
 * @returns {Array<string>} Lista de chaves TIPO_CORRECAO disponíveis
 */
const calcularTipoCorrecaoPosObra = (params = {}) => {
  const { quantidadeParcelas, eSAD = false, regrasExtra = [] } = params;

  const isParcelas48OrLess = quantidadeParcelas <= 48;
  const isParcelas49OrMore = quantidadeParcelas >= 49;

  let correcaoOptions = [];

  if (eSAD) {
    // 1) Correção padrão SAD
    correcaoOptions = ['SAD_IPCA_099'];

    // 2) Correções extras tipo 5 (CORRECAO_SAD) vindas do ADM
    const correcoesExtras = extrairRegrasDoTipo(regrasExtra, TIPO_REGRA_EXTRA.CORRECAO_SAD, NOME_REGRA_TO_CORRECAO_KEY);
    correcoesExtras.forEach(c => {
      if (!correcaoOptions.includes(c)) correcaoOptions.push(c);
    });
  } else {
    // 1) Correção padrão Linear (baseada em parcelas)
    if (isParcelas48OrLess) {
      correcaoOptions.push('IPCA_085');
    }
    if (isParcelas49OrMore) {
      correcaoOptions.push('IPCA_099');
    }

    // 2) Correções extras tipo 6 (CORRECAO_LINEAR) vindas do ADM
    const correcoesExtras = extrairRegrasDoTipo(regrasExtra, TIPO_REGRA_EXTRA.CORRECAO_LINEAR, NOME_REGRA_TO_CORRECAO_KEY);
    correcoesExtras.forEach(c => {
      if (!correcaoOptions.includes(c)) correcaoOptions.push(c);
    });
  }

  console.log('[TipoCorrecao] Pós Obra calculado - Parcelas:', quantidadeParcelas, 'eSAD:', eSAD, 'regrasExtra:', regrasExtra, '→', correcaoOptions);

  return correcaoOptions;
};

/**
 * Calcula os tipos de correções disponíveis baseado no plano selecionado
 * @param {string} planoSelecionado - Nome do plano selecionado (ex: 'À Vista', 'Prazo de Obra')
 * @param {object} additionalParams - Parâmetros adicionais para regras complexas (uso futuro)
 * @returns {Array<string>} Lista de chaves TIPO_CORRECAO disponíveis
 */
export const calcularTipoCorrecoesDisponiveis = (planoSelecionado, additionalParams = {}) => {
  if (!planoSelecionado) {
    return []; // Sem plano selecionado, sem correções
  }

  // Planos com correção "Sem Correção"
  const planosSemCorrecao = [
    ALL_PLANOS.AVista,
    ALL_PLANOS.Fixo12,
    ALL_PLANOS.Fixo18
  ];

  // Verificar se o plano não tem correção
  if (planosSemCorrecao.includes(planoSelecionado)) {
    console.log('[TipoCorrecao] Plano sem correção:', planoSelecionado, '→ [SemCorrecao]');
    return ['SemCorrecao'];
  }

  // Planos com regras específicas
  let tiposDisponiveis = [];

  switch (planoSelecionado) {
    case ALL_PLANOS.PrazoObra:
      // Prazo de Obra - sempre IPCA
      tiposDisponiveis = ['IPCA'];
      console.log('[TipoCorrecao] Prazo de Obra:', planoSelecionado, '→', tiposDisponiveis);
      break;

    case ALL_PLANOS.FinanciamentoBancario: {
      // 1) Correção padrão FB
      tiposDisponiveis = ['INCC_IPCA_1'];

      // 2) Correções extras tipo 4 (CORRECAO_FB) vindas do ADM
      const correcoesExtrasFB = extrairRegrasDoTipo(
        additionalParams.regrasExtra ?? [],
        TIPO_REGRA_EXTRA.CORRECAO_FB,
        NOME_REGRA_TO_CORRECAO_KEY
      );
      correcoesExtrasFB.forEach(c => {
        if (!tiposDisponiveis.includes(c)) tiposDisponiveis.push(c);
      });

      console.log('[TipoCorrecao] Financiamento Bancário:', planoSelecionado, '→', tiposDisponiveis);
      break;
    }

    case ALL_PLANOS.PosObra:
      // Regras de Tipo de Correção para Pós Obra
      tiposDisponiveis = calcularTipoCorrecaoPosObra(additionalParams);
      console.log('[TipoCorrecao] Pós Obra:', planoSelecionado, '→', tiposDisponiveis, 'params:', additionalParams);
      break;

    default:
      console.warn('[TipoCorrecao] Plano não reconhecido:', planoSelecionado);
      tiposDisponiveis = ['SemCorrecao'];
      break;
  }

  console.log('[TipoCorrecao] Calculando para plano:', planoSelecionado, '→', tiposDisponiveis);
  return tiposDisponiveis;
}